<template>
  <div class="product-card-line q-px-sm q-py-sm" v-bind="bindContainer">
    <a
      class="card-content bg-white row q-col-gutter-xs items-center full-width"
      :is="bindLinkComponent"
      v-bind="bindLink"
      v-on="{ ...bindLinkListeners, ...dragListeners }"
    >
      <!-- Медиа блок -->
      <div class="card-media col-5">
        <div class="media-thumb">
          <q-img v-if="!debugOptions.disableImages" fit="contain" :src="imageUrl" class="thumb-img" />
        </div>
      </div>

      <!-- Контент -->
      <div class="card-main col-14">
        <!-- Заголовок -->
        <div class="text-weight-bold s-font-2xs leading-none">
          {{ product.NAME }}
        </div>

        <!-- Информер о модификаторах (если товар - опция модификатора) -->
        <ModificatorRequirement
          v-if="showModificatorRequirement"
          :modificator-name="modificatorRequirementData?.modificatorName"
          :is-global="modificatorRequirementData?.isGlobal"
          :price-remaining="modificatorRequirementData?.priceRemaining"
          :progress-percent="modificatorRequirementData?.progressPercent"
          :min-order-price="modificatorRequirementData?.minOrderPrice"
          :is-available="modificatorRequirementData?.isAvailable || false"
        />
        <!-- Описание (только если НЕ опция модификатора) -->
        <div
          v-else-if="descTeaser"
          class="c-desc text-grey-7 s-font-2xs s-font-md-3xs leading-3"
          v-html="descTeaser"
        />

      </div>

      <div class="col-5">
        <q-no-ssr>
          <!-- Режим селектора подарков -->
          <div
            v-if="props.giftSelectorMode"
            class="gift-selector-indicator text-center"
          >
            <!-- Показываем иконку подарка если подарок выбран -->
            <div v-if="props.isGiftSelected" class="gift-selected text-positive">
              <q-icon :name="ICONS.gift" size="32px" />
            </div>
            <!-- Иначе показываем placeholder -->
            <div v-else class="gift-not-selected text-grey-5">
              <q-icon :name="ICONS.gift" size="32px" />
            </div>
          </div>

          <!-- Если товар недоступен - показываем только цену -->
          <div
            v-else-if="isProductUnavailable"
            class="price-only text-center leading-none"
          >
            <div class="s-font-sm">за</div>
            <div>
              <AnimatedNumber
                :number="totalPrice"
                append=" ₽"
                class="s-font-xs text-weight-bold text-primary"
              />
            </div>
          </div>

          <!-- Если товар доступен - показываем кнопку с корзиной -->
          <div
            v-else
            class="basket-add-btn text-primary text-center"
            @click.prevent="onBasketAdd"
          >
            <q-spinner v-if="basketMutating" size="10px" />
            <template v-else>
              <div>
                <svg>
                  <use xlink:href="/static/img/icons.svg#basket"></use>
                </svg>
              </div>
              <div>
                <AnimatedNumber
                  :number="totalPrice"
                  append=" ₽"
                  class="s-font-xs text-weight-bold"
                />
              </div>
            </template>
          </div>
        </q-no-ssr>
      </div>
    </a>
  </div>
</template>

<script setup lang="ts">
import type { SuggestedModificatorProduct } from "@/modules/shop/store/basket";
import { useCurrency } from "@/modules/shop/store/currency";
import { computed } from "vue";
import { ProductCardProps, useProductView } from "./hooks";
import ModificatorRequirement from "./ui/ModificatorRequirement.vue";
import { useDebugStore } from "@/core/store/debug";
import { storeToRefs } from "pinia";
import {QNoSsr} from "quasar";
import {ICONS} from "@/assets/icons";

export interface ProductCardLineProps extends ProductCardProps {
  // Данные о модификаторе (если товар является опцией модификатора)
  modificatorSuggestion?: SuggestedModificatorProduct;
  // Режим селектора подарков (скрывает кнопку добавления в корзину)
  giftSelectorMode?: boolean;
  // Выбран ли подарок (показывает галочку вместо корзины)
  isGiftSelected?: boolean;
}

const props = withDefaults(defineProps<ProductCardLineProps>(), {
  mediaBg: true,
});

const {
  isDragging,
  totalPrice,
  product,
  onBasketAdd,
  basketItem,
  bindContainer,
  descTeaser,
  imageUrl,
  bindLinkComponent,
  bindLink,
  bindLinkListeners,
  basketMutating,
  quantityLimit,
  dragListeners,
  cancelClick,
  isModificatorOption,
  isAvailableToAdd,
  productModificatorStates,
} = useProductView(props, {
  quantityLimit: props.quantityLimit,
  openDragDetect: true, // Включаем защиту от drag
  calcOptions: {
    round: true,
  },
  imageSize: "s-1",
});

const { format } = useCurrency();

// Debug store для опций отладки
const debugStore = useDebugStore();
const { debugOptions } = storeToRefs(debugStore);

// Вычисляем allowInc с учетом quantityLimit
const allowInc = computed(() => {
  if (!quantityLimit) return true;
  return basketItem.value.fields.QUANTITY < quantityLimit;
});

// Показывать ли информер о модификаторах
// Информер показываем ТОЛЬКО для недоступных товаров с restricted модификаторами
// Requirements: 4.2
const showModificatorRequirement = computed(() => {
  // Если передан modificatorSuggestion
  if (props.modificatorSuggestion) {
    // Показываем информер только если товар недоступен
    return !props.modificatorSuggestion.isAvailable;
  }

  // Показываем если товар является опцией модификатора И недоступен
  // Для restricted модификаторов (special, constructor) показываем требования
  return isModificatorOption.value && !isAvailableToAdd.value;
});

// Данные для информера модификаторов
const modificatorRequirementData = computed(() => {
  // Если передан modificatorSuggestion - используем его
  if (props.modificatorSuggestion) {
    const suggestion = props.modificatorSuggestion;
    return {
      modificatorName: suggestion.modificator.NAME,
      isGlobal: suggestion.modificator.IS_GLOBAL,
      priceRemaining: suggestion.priceRemaining,
      progressPercent: suggestion.progressPercent,
      minOrderPrice: suggestion.modificator.ORDER_PRICE_FROM ?? undefined,
      isAvailable: suggestion.isAvailable,
    };
  }

  // Иначе используем новое API - productModificatorStates из hooks
  if (isModificatorOption.value && productModificatorStates.value.length > 0) {
    // Берем первый модификатор (обычно товар связан с одним модификатором)
    const state = productModificatorStates.value[0];
    if (state) {
      return {
        modificatorName: state.modificator.NAME,
        isGlobal: state.modificator.IS_GLOBAL,
        priceRemaining: state.priceRemaining,
        progressPercent: state.progress * 100, // Конвертируем 0-1 в 0-100
        minOrderPrice: state.modificator.ORDER_PRICE_FROM ?? undefined,
        isAvailable: state.active && state.hasAvailableProducts,
      };
    }
  }

  return null;
});

// Проверка доступности товара для добавления в корзину
const isProductUnavailable = computed(() => {
  // Если передан modificatorSuggestion - проверяем его isAvailable
  if (props.modificatorSuggestion) {
    return !props.modificatorSuggestion.isAvailable;
  }

  // Иначе используем данные из hooks
  return !isAvailableToAdd.value;
});
</script>

<style scoped lang="scss">
.product-card-line {
  box-shadow: 2px 3px 8px 0px rgba(119, 119, 119, 0.2);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.basket-add-btn {
  line-height: 0.85;
  cursor: pointer;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 2px 4px;
  align-items: center;
  justify-content: center;
  width: 100%;

  svg,
  img {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
}

.c-desc {
  margin-top: 3px;
  max-height: 37px;
  overflow: hidden;
}

.gift-selector-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  .gift-selected {
    animation: checkmark-appear 0.3s ease;
  }

  .gift-not-selected {
    opacity: 0.5;
  }
}

@keyframes checkmark-appear {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
